import { TableProps, Tooltip } from 'antd';
import { useWindowSize } from 'usehooks-ts';

import { Spin, Table } from 'components/common';

import { usePerformance } from '../../modules/performance/usePerformance';
import { getYScroll } from '../XandrSettings/utils';
import { AllTouchPreviewTableProps } from './ReportsTypes';

export const AdformAllTouchPreviewTable = ({
  pageSize,
}: Pick<AllTouchPreviewTableProps, 'pageSize'>) => {
  const { height = 0 } = useWindowSize();
  const { allTouch, isAllTouchPreviewLoading } = usePerformance();

  const renderEllipsisWithTooltip = (text: string) => (
    <Tooltip title={text}>
      <span
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          wordBreak: 'break-all',
        }}
      >
        {text}
      </span>
    </Tooltip>
  );

  const columns: TableProps['columns'] = [
    {
      dataIndex: 'uniqueId',
      title: 'Unique ID',
      fixed: 'left',
      width: 192,
      ellipsis: true,
      render: renderEllipsisWithTooltip,
    },
    {
      dataIndex: 'pixelDateHourUtc',
      title: 'Conversion Timestamp',
      width: 192,
      ellipsis: true,
      render: renderEllipsisWithTooltip,
    },
    {
      dataIndex: 'impDateTimeUtc',
      title: 'Impression Timestamp',
      width: 192,
      ellipsis: true,
      render: renderEllipsisWithTooltip,
    },
    {
      dataIndex: 'subcategory',
      title: 'Subcategory',
      width: 128,
    },
    {
      dataIndex: 'campaignName',
      title: 'Campaign',
      width: 192,
      ellipsis: true,
      render: (text: string) => text || 'N/A',
    },
    {
      dataIndex: 'lineItemName',
      title: 'Line Item',
      width: 192,
      ellipsis: true,
      render: (text: string) => text || 'N/A',
    },
    {
      dataIndex: 'creativeName',
      title: 'Creative',
      width: 192,
      ellipsis: true,
      render: (text: string) => text || 'N/A',
    },
    {
      dataIndex: 'siteName',
      title: 'Site Name',
      width: 256,
      ellipsis: true,
      render: renderEllipsisWithTooltip,
    },
    {
      dataIndex: 'cityName',
      title: 'City',
      width: 128,
    },
    {
      dataIndex: 'deviceTypeName',
      title: 'Device Type',
      width: 128,
    },
    {
      dataIndex: 'index',
      title: 'Times Exposed',
      width: 128,
    },
    {
      dataIndex: 'timeToConvert',
      title: 'Time to Convert (hrs)',
      width: 128,
    },
    {
      dataIndex: 'hashedIp',
      title: 'Hashed IP',
      width: 128,
      ellipsis: true,
      render: renderEllipsisWithTooltip,
    },
  ];

  const totalWidth = columns.reduce((sum, col) => sum + ((col.width as number) || 100), 0);

  return (
    <Spin spinning={isAllTouchPreviewLoading}>
      <Table
        className="overflow-x-auto"
        dataSource={allTouch?.data.map((data, index) => ({
          ...data,
          key: data.uniqueId ?? `row-${index}`,
          children: Array.isArray(data.impressions)
            ? data.impressions.map((imp, i) => ({
                ...imp,
                key: `child-${data.uniqueId}-${i}`,
              }))
            : [],
        }))}
        columns={columns}
        pagination={false}
        tableLayout="fixed"
        style={{ width: '100%' }}
        scroll={{ x: totalWidth, ...getYScroll(pageSize, height) }}
        expandable={{
          expandedRowClassName: () => 'ctv-expanded-row',
        }}
      />
    </Spin>
  );
};
